// next-app/src/lib/validations/event-planning.ts
import { z } from 'zod';
import {
  emailSchema,
  phoneSchema,
  address1Schema,
  citySchema,
  zipSchema,
  requiredSchema,
} from './validators';

// ============================================
// EVENT PLANNING FORM
// Reference: src/pages/EventPlanningForm.tsx
// ============================================

const eventTypeValues = ['basic', 'test_drive', 'walkaround'] as const;

// Schema for the event planning form (save action - fields are optional)
export const eventPlanningSchema = z.object({
  // Event Basics
  event_name: z.string().max(25),
  event_type: z
    .object({
      text: z.string(),
      value: z.enum(eventTypeValues),
    })
    .optional()
    .nullable(),
  funding_purpose: z.string(),

  // File Upload (handled separately, not in Zod)
  // event_plan_file is managed via state

  // Date & Time
  time_zone: z.string(),
  start_date: z.date().optional().nullable(),
  start_time: z.date().optional().nullable(),
  end_time: z.date().optional().nullable(),

  // Location
  event_location_name: z.string().max(255),
  address: z.string().max(45),
  city: z.string().max(45),
  state: z.string(),
  zip_code: z.string(),

  // Contact
  location_contact: z.string().max(100),
  location_phone: z.string(),
  location_email: z.string(),

  // Attendance
  attendance: z.number().optional().nullable(),

  // Terms (required only on final submit)
  agree_terms: z.boolean(),
});

export type EventPlanningFormData = z.infer<typeof eventPlanningSchema>;

// Schema for final submission validation (all fields required)
export const eventPlanningSubmitSchema = z.object({
  event_name: requiredSchema.max(25),
  event_type: z.object({
    text: z.string(),
    value: z.enum(eventTypeValues),
  }),
  funding_purpose: requiredSchema,
  time_zone: requiredSchema,
  start_date: z.date({ message: 'Event date is required' }),
  start_time: z.date({ message: 'Start time is required' }),
  end_time: z.date({ message: 'End time is required' }),
  event_location_name: requiredSchema.max(255),
  address: address1Schema.max(45),
  city: citySchema.max(45),
  state: requiredSchema,
  zip_code: zipSchema,
  location_contact: requiredSchema.max(100),
  location_phone: phoneSchema,
  location_email: emailSchema,
  attendance: z.number({ message: 'Attendance is required' }).min(1),
  agree_terms: z.literal(true, { message: 'You must agree to the terms' }),
});

export type EventPlanningSubmitData = z.infer<typeof eventPlanningSubmitSchema>;
